import { directionFromScreen, screenBearing } from "../core/direction";
import type { Game } from "../core/game";
import { DIRECTIONS, type Direction } from "../core/types";
import { ISO_PITCH, ISO_YAW } from "./layout";

/**
 * Touch steering: a thumb pad, and a swipe anywhere on the deck.
 *
 * Two controls rather than one because they fail in opposite situations. The
 * pad is precise and always in the same place, but it covers a corner of the
 * screen and needs the thumb to travel there. A swipe is instant and can be
 * made anywhere, but it cannot be held or aimed carefully. Arcade play wants
 * both, and the game core only ever receives `steer(dir)` either way.
 *
 * Every button is PLACED and ROTATED from `screenBearing`, never from a table
 * of four arrow glyphs. The camera is isometric, so grid east is screen
 * down-right; a pad drawn as a plus would point at four headings the maze does
 * not have. If the camera bearing is ever retuned, the pad follows it.
 */

/** How far a drag must travel before it counts as a swipe rather than a tap. */
const SWIPE_PX = 26;

/** Half the pad's width, as a fraction of the pad box. Buttons sit on this ring. */
const RING = 0.32;

const LABELS: Readonly<Record<Direction, string>> = {
  up: "north",
  down: "south",
  left: "west",
  right: "east",
};

export interface TouchControls {
  /** Reveal the pad. Called for real touches; harmless if already shown. */
  reveal(): void;
  /** Unbind everything and remove the pad. */
  destroy(): void;
}

/**
 * Bind touch steering to `game`.
 *
 * `surface` is what a swipe is read from -- the canvas, so a drag that starts
 * on a HUD panel is not mistaken for steering.
 */
export const bindTouchControls = (game: Game, surface: HTMLElement, parent: HTMLElement = document.body): TouchControls => {
  const pad = document.createElement("div");
  pad.id = "touchpad";
  pad.dataset["testid"] = "touchpad";
  pad.hidden = true;

  const press = (dir: Direction, button: HTMLElement) => (event: PointerEvent): void => {
    // Steer on DOWN, not on click: waiting for the release adds the player's
    // whole press duration to the input latency, and a junction goes past.
    event.preventDefault();
    game.steer(dir);
    button.dataset["held"] = "true";
    button.setPointerCapture(event.pointerId);
  };

  const release = (button: HTMLElement) => (): void => {
    delete button.dataset["held"];
  };

  const teardown: Array<() => void> = [];

  for (const dir of DIRECTIONS) {
    const bearing = screenBearing(dir, ISO_YAW, ISO_PITCH);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "touchpad-key";
    button.dataset["testid"] = `touch-${dir}`;
    button.dataset["dir"] = dir;
    button.setAttribute("aria-label", `Steer ${LABELS[dir]}`);
    // Placed on the projected ring and leaned to match, so the arrow points
    // exactly along the corridor it will send the astronaut down.
    button.style.left = `${50 + Math.cos(bearing) * RING * 100}%`;
    button.style.top = `${50 + Math.sin(bearing) * RING * 100}%`;
    button.style.setProperty("--lean", `${bearing}rad`);
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h13m0 0-5-5m5 5-5 5"/></svg>`;

    const down = press(dir, button);
    const up = release(button);
    button.addEventListener("pointerdown", down);
    button.addEventListener("pointerup", up);
    button.addEventListener("pointercancel", up);
    // A touch that starts on the pad must never also scroll or zoom the page.
    button.addEventListener("contextmenu", (e) => e.preventDefault());
    teardown.push(() => {
      button.removeEventListener("pointerdown", down);
      button.removeEventListener("pointerup", up);
      button.removeEventListener("pointercancel", up);
    });
    pad.append(button);
  }

  parent.append(pad);

  const reveal = (): void => {
    pad.hidden = false;
    document.body.dataset["touch"] = "true";
  };

  // --- swipe ------------------------------------------------------------
  // Anchored rather than absolute: each time a swipe fires, the anchor moves
  // to where the thumb is now, so one continuous drag can turn a corner and
  // then turn again without lifting off.
  let anchorX = 0;
  let anchorY = 0;
  let tracking: number | null = null;
  let last: Direction | null = null;

  const onDown = (event: PointerEvent): void => {
    if (event.pointerType === "touch") reveal();
    if (event.pointerType === "mouse") return; // desktop drags are not steering
    tracking = event.pointerId;
    anchorX = event.clientX;
    anchorY = event.clientY;
    last = null;
  };

  const onMove = (event: PointerEvent): void => {
    if (tracking !== event.pointerId) return;
    const dx = event.clientX - anchorX;
    const dy = event.clientY - anchorY;
    if (Math.hypot(dx, dy) < SWIPE_PX) return;
    const dir = directionFromScreen(dx, dy, ISO_YAW, ISO_PITCH);
    anchorX = event.clientX;
    anchorY = event.clientY;
    if (dir === null || dir === last) return;
    last = dir;
    game.steer(dir);
  };

  const onUp = (event: PointerEvent): void => {
    if (tracking === event.pointerId) tracking = null;
  };

  surface.addEventListener("pointerdown", onDown);
  surface.addEventListener("pointermove", onMove);
  surface.addEventListener("pointerup", onUp);
  surface.addEventListener("pointercancel", onUp);

  // A device that reports a coarse pointer gets the pad immediately: waiting
  // for a first touch would leave a phone player looking at a game with no
  // visible way to play it. Everything else waits, so a laptop with a
  // touchscreen is not given a thumb pad it will never use.
  if (window.matchMedia("(pointer: coarse)").matches) reveal();

  return {
    reveal,
    destroy: () => {
      for (const off of teardown) off();
      surface.removeEventListener("pointerdown", onDown);
      surface.removeEventListener("pointermove", onMove);
      surface.removeEventListener("pointerup", onUp);
      surface.removeEventListener("pointercancel", onUp);
      pad.remove();
      delete document.body.dataset["touch"];
    },
  };
};
